import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Typography, Box, Chip, Stack, CircularProgress,
    Button, Grid, Paper, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useGetTeacher } from '../../hooks/teachers/useGetTeacher';
import { useTranslation } from 'react-i18next';

const ModelShowTeacher = ({ open, onClose, teacherId }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const theme = useTheme();

    const { teacher: data, isLoading } = useGetTeacher(teacherId);
    const t = data?.data;
    const fmt = (v) => v || '-';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3, bgcolor: theme.palette.background.card } }}>
            <DialogTitle sx={{ m: 0, px: 3, py: 2, borderBottom: (th) => `1px solid ${th.palette.divider}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{isArabic ? 'تفاصيل المعلم' : 'Teacher Details'}</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ pt: 2, pb: 3, px: 3, direction: isArabic ? 'rtl' : 'ltr' }}>
                {isLoading || !teacherId ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>
                ) : !t ? (
                    <Typography align="center" sx={{ py: 4 }}>{isArabic ? 'لا توجد بيانات' : 'No data'}</Typography>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: theme.palette.divider }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="h6" fontWeight={600}>{t.employee_name}</Typography></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'المدرسة' : 'School'} value={fmt(t.school_name)} /></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'القسم' : 'Department'} value={fmt(t.department_name)} /></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'المسمى' : 'Job Title'} value={fmt(t.job_title_name)} /></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'الجنس' : 'Gender'} value={t.gender === 'male' ? (isArabic ? 'ذكر' : 'Male') : (isArabic ? 'أنثى' : 'Female')} /></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'الهاتف' : 'Phone'} value={fmt(t.phone)} /></Grid>
                            <Grid item xs={6}><Field label={isArabic ? 'الأجزاء المحفوظة' : 'Memorized Parts'} value={`${t.memorized_parts_count ?? 0} / 30`} /></Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">{isArabic ? 'إجازة' : 'Ijazah'}</Typography>
                                <Box>{t.has_ijazah
                                    ? <Chip icon={<CheckCircleIcon />} label={isArabic ? 'نعم' : 'Yes'} size="small" color="success" variant="outlined" />
                                    : <Chip icon={<CancelIcon />} label={isArabic ? 'لا' : 'No'} size="small" color="default" variant="outlined" />}
                                </Box>
                            </Grid>
                            {t.ijazah_notes && (
                                <Grid item xs={12}><Field label={isArabic ? 'ملاحظات الإجازة' : 'Ijazah Notes'} value={t.ijazah_notes} /></Grid>
                            )}
                            <Grid item xs={12}>
                                <Chip label={t.is_active ? (isArabic ? 'مفعّل' : 'Active') : (isArabic ? 'موقوف' : 'Inactive')}
                                    size="small" color={t.is_active ? 'success' : 'error'} variant="outlined" />
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, borderTop: (th) => `1px solid ${th.palette.divider}` }}>
                <Button onClick={onClose} variant="contained">{isArabic ? 'إغلاق' : 'Close'}</Button>
            </DialogActions>
        </Dialog>
    );
};

const Field = ({ label, value }) => (<><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2" fontWeight={500}>{value}</Typography></>);

export default ModelShowTeacher;
